import React, { Children } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Flex } from
'@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
export function PrivacyPolicyPage() {
  return (
    <Box
      as="section"
      py={{
        base: 12,
        md: 20
      }}>
      
      <Container maxW="container.xl" px={4}>
        <Box maxW="3xl" mx="auto">
          <Flex
            as={RouterLink}
            to="/"
            display="inline-flex"
            align="center"
            gap={2}
            fontSize="sm"
            fontWeight="semibold"
            color="gray.400"
            _hover={{
              color: 'white'
            }}
            transition="colors 0.2s"
            mb={10}>
            
            <ArrowLeft size={16} />
            Back to Homepage
          </Flex>

          <Heading
            as="h1"
            fontSize={{
              base: '3xl',
              md: '4xl'
            }}
            fontWeight="bold"
            mb={2}>
            
            Privacy Policy
          </Heading>
          <Text fontSize="sm" color="gray.500" mb={10}>
            Last updated: April 8, 2026
          </Text>

          <VStack
            spacing={8}
            align="stretch"
            color="gray.300"
            lineHeight="relaxed">
            
            <Box>
              <Heading
                as="h2"
                fontSize="xl"
                fontWeight="bold"
                color="white"
                mb={3}>
                
                1. Information We Collect
              </Heading>
              <Text mb={3}>We collect the following types of information:</Text>
              <UnorderedList
                spacing={2}
                color="gray.400"
                stylePosition="inside">
                
                <ListItem>
                  <Text as="strong" color="gray.300">
                    Account Information:
                  </Text>{' '}
                  Name, email address, and optional Telegram ID provided during
                  registration or access requests
                </ListItem>
                <ListItem>
                  <Text as="strong" color="gray.300">
                    Store Data:
                  </Text>{' '}
                  URLs and publicly accessible product page content submitted
                  for scanning
                </ListItem>
                <ListItem>
                  <Text as="strong" color="gray.300">
                    Usage Data:
                  </Text>{' '}
                  Information about how you interact with the Service, including
                  scan history and feature usage
                </ListItem>
                <ListItem>
                  <Text as="strong" color="gray.300">
                    Device Information:
                  </Text>{' '}
                  Browser type, operating system, and IP address collected
                  automatically
                </ListItem>
              </UnorderedList>
            </Box>

            <Box>
              <Heading
                as="h2"
                fontSize="xl"
                fontWeight="bold"
                color="white"
                mb={3}>
                
                2. How We Use Your Information
              </Heading>
              <Text mb={3}>We use the information we collect to:</Text>
              <UnorderedList
                spacing={2}
                color="gray.400"
                stylePosition="inside">
                
                <ListItem>Provide, maintain, and improve the Service</ListItem>
                <ListItem>
                  Process your scan requests and generate compliance reports
                </ListItem>
                <ListItem>
                  Communicate with you about your account, updates, and support
                </ListItem>
                <ListItem>
                  Monitor and analyse usage trends to improve user experience
                </ListItem>
                <ListItem>
                  Detect and prevent fraud, abuse, or security incidents
                </ListItem>
              </UnorderedList>
            </Box>

            {[
            {
              title: '3. Data Sharing',
              content:
              'We do not sell, rent, or trade your personal information to third parties. We may share information with trusted service providers who assist in operating the Service (e.g., hosting, analytics), subject to confidentiality obligations. We may also disclose information if required by law or to protect our rights and safety.'
            },
            {
              title: '4. Data Retention',
              content:
              'We retain your personal information for as long as your account is active or as needed to provide the Service. Scan results and reports are retained for a reasonable period to allow you to access historical data. You may request deletion of your account and associated data at any time by contacting us.'
            },
            {
              title: '5. Data Security',
              content:
              'We implement appropriate technical and organisational measures to protect your information against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.'
            },
            {
              title: '6. Cookies',
              content:
              'We may use cookies and similar tracking technologies to enhance your experience, analyse usage patterns, and remember your preferences. You can control cookie settings through your browser, though disabling cookies may affect certain features of the Service.'
            }].
            map((section, i) =>
            <Box key={i}>
                <Heading
                as="h2"
                fontSize="xl"
                fontWeight="bold"
                color="white"
                mb={3}>
                
                  {section.title}
                </Heading>
                <Text>{section.content}</Text>
              </Box>
            )}

            <Box>
              <Heading
                as="h2"
                fontSize="xl"
                fontWeight="bold"
                color="white"
                mb={3}>
                
                7. Your Rights
              </Heading>
              <Text mb={3}>
                Depending on your jurisdiction, you may have the right to:
              </Text>
              <UnorderedList
                spacing={2}
                color="gray.400"
                stylePosition="inside">
                
                <ListItem>
                  Access the personal information we hold about you
                </ListItem>
                <ListItem>
                  Request correction of inaccurate information
                </ListItem>
                <ListItem>
                  Request deletion of your personal information
                </ListItem>
                <ListItem>
                  Object to or restrict certain processing of your data
                </ListItem>
                <ListItem>Request portability of your data</ListItem>
              </UnorderedList>
            </Box>

            {[
            {
              title: '8. Third-Party Links',
              content:
              'The Service may contain links to third-party websites or services. We are not responsible for the privacy practices or content of these third parties. We encourage you to review the privacy policies of any third-party sites you visit.'
            },
            {
              title: "9. Children's Privacy",
              content:
              'The Service is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete it promptly.'
            },
            {
              title: '10. Changes to This Policy',
              content:
              'We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page with a revised "Last updated" date. Your continued use of the Service after changes are posted constitutes acceptance of the updated policy.'
            },
            {
              title: '11. Contact',
              content:
              'If you have any questions or concerns about this Privacy Policy, please contact us through the Request Access page or reach out to our support team.'
            }].
            map((section, i) =>
            <Box key={i}>
                <Heading
                as="h2"
                fontSize="xl"
                fontWeight="bold"
                color="white"
                mb={3}>
                
                  {section.title}
                </Heading>
                <Text>{section.content}</Text>
              </Box>
            )}
          </VStack>
        </Box>
      </Container>
    </Box>);

}